(function BirthdayOffer() {
  "use strict";

  // Configuration - Expanded with constants
  const CONFIG = {
    storageKey: "birthdayOfferSeen",
    storageKeyDate: "birthdayOfferDate",
    delay: 1000,
    mobileBreakpoint: 768,
    animationDelays: {
      fadeOut: 300,
      expansion: 500,
      flip: 100,
    },
    selectors: {
      modal: "#birthday-offer-modal",
      cards: ".birthday-card",
      closeBtn: ".birthday-offer-close",
      backdrop: ".birthday-offer-backdrop",
      rewardDisplay: ".birthday-reward-display",
      continueBtn: ".birthday-continue-button",
      discountContainer: ".birthday-reward-discount",
      productContainer: ".birthday-reward-product",
      bundleContainer: ".birthday-reward-bundle",
      codeText: ".birthday-code-text",
      copyBtn: ".birthday-copy-button",
      addToCartBtn: ".birthday-add-to-cart-button",
      addBundleBtn: ".birthday-add-to-cart-button",
      productTitle: ".birthday-product-title",
      bundleTitle: ".birthday-bundle-title",
      productImage: ".birthday-product-image",
      bundleProductsGrid: ".bundle-products-grid",
      rewardTitle: ".birthday-reward-title",
      message: ".birthday-offer-message",
      cardsContainer: ".birthday-offer-cards",
      cartBubble: ".cart-count-bubble",
      miniCart: "mini-cart",
      cartDrawer: "cart-drawer",
      cartIcon:
        ".header__icon--cart, [data-cart-toggle], .cart-toggle, #cart-icon-bubble",
      reopenBtn: ".birthday-reopen-button",
    },
    propertyMessages: {
      addon:
        "Birthday Gift: This gift can only be redeemed alongside a purchase. Please add another product to the cart to proceed",
      standalone: "Birthday Gift",
    },
    errorMessages: {
      422: "Product Not Available",
      404: "Product Not Found",
      network: "Network Error - Check Connection",
      productNotFound: "Product Not Found",
      configError: "Product Configuration Error",
      dataError: "Product Data Error",
    },
  };

  // State management
  let state = {
    modal: null,
    cards: [],
    selectedCard: null,
    rewards: [],
    isActive: false,
    isProcessing: false, // Add processing flag
    cachedElements: {}, // Cache for DOM elements
    hasFlippedCard: false, // Track if user has flipped a card
  };

  // Utility functions
  const utils = {
    // Fisher-Yates shuffle for better performance
    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },

    // Debounce function
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // Get cached element or query and cache it
    getElement(selector, context = state.modal) {
      if (!state.cachedElements[selector]) {
        state.cachedElements[selector] = context
          ? context.querySelector(selector)
          : document.querySelector(selector);
      }
      return state.cachedElements[selector];
    },

    // Clear element cache
    clearElementCache() {
      state.cachedElements = {};
    },
  };

  // Initialize the birthday offer
  async function init() {
    // Check if modal exists
    const modal = document.getElementById("birthday-offer-modal");
    if (!modal) return;

    // Check if already seen or if cart has reward products
    if (await shouldSkipOffer()) return;

    // Setup state
    state.modal = modal;
    state.cards = Array.from(modal.querySelectorAll(CONFIG.selectors.cards));
    state.rewards = getRewardsData();

    // Check B2B status (for internal use)
    const isB2B = isB2BUser();

    // Assign card images in order
    assignCardImages();

    // Setup event listeners
    setupEventListeners();

    // Show modal after delay
    setTimeout(() => {
      showModal();
    }, CONFIG.delay);
  }

  // Check if we should skip showing the offer
  async function shouldSkipOffer() {
    const frequency = state.modal?.dataset.frequency || "session";

    // First check if customer already has a reward product in cart
    if (await hasRewardProductInCart()) {
      return true;
    }

    if (frequency === "session") {
      return sessionStorage.getItem(CONFIG.storageKey) === "true";
    } else if (frequency === "day") {
      const today = new Date().toDateString();
      const lastSeen = sessionStorage.getItem(CONFIG.storageKeyDate);
      return lastSeen === today;
    }

    return false;
  }

  // Check if cart contains any birthday reward products
  async function hasRewardProductInCart() {
    try {
      // Try to get cart data from DOM first (faster)
      const cartData = getCartDataFromDOM();

      if (cartData && cartData.items && cartData.items.length > 0) {
        return analyzeCartForRewards(cartData);
      }

      // Fallback to API if DOM data not available
      const response = await fetch("/cart.js");
      const apiCartData = await response.json();

      return analyzeCartForRewards(apiCartData);
    } catch (error) {
      console.warn("Failed to check cart for reward products:", error);
      return false; // Allow popup to show if we can't check cart
    }
  }

  // Get cart data from DOM (similar to cart-validation.js)
  function getCartDataFromDOM() {
    try {
      const cartItems = document.querySelectorAll(
        ".cart-item, .cart__item, [data-cart-item]"
      );

      if (cartItems.length === 0) {
        return null;
      }

      const items = [];

      cartItems.forEach((cartItem, index) => {
        const properties = {};

        // Look for property elements in the cart item
        const propertyElements = cartItem.querySelectorAll(
          ".product-option, [data-property]"
        );
        propertyElements.forEach((propElement) => {
          const dt = propElement.querySelector("dt");
          const dd = propElement.querySelector("dd");
          if (dt && dd) {
            const key = dt.textContent.replace(":", "").trim();
            const value = dd.textContent.trim();
            properties[key] = value;
          }
        });

        // Also check for any hidden input fields with properties
        const hiddenInputs = cartItem.querySelectorAll('input[type="hidden"]');
        hiddenInputs.forEach((input) => {
          const name = input.name;
          if (name && name.includes("properties[")) {
            const key = name.replace("properties[", "").replace("]", "");
            properties[key] = input.value;
          }
        });

        items.push({
          properties: properties,
          index: index,
        });
      });

      return { items: items };
    } catch (error) {
      console.error("Error reading cart data from DOM:", error);
      return null;
    }
  }

  // Analyze cart data for reward products (similar to cart-validation.js)
  function analyzeCartForRewards(cartData) {
    // Check for any birthday reward products (both addon and standalone)
    const hasRewardProducts = cartData.items.some(
      (item) =>
        item.properties &&
        Object.keys(item.properties).some(
          (key) => key.includes("Birthday Gift") && item.properties[key] === "✓"
        )
    );

    return hasRewardProducts;
  }

  // Mark offer as seen
  function markOfferAsSeen() {
    const frequency = state.modal?.dataset.frequency || "session";

    if (frequency === "session") {
      sessionStorage.setItem(CONFIG.storageKey, "true");
      // Also set the date for tracking purposes
      const today = new Date().toDateString();
      sessionStorage.setItem(CONFIG.storageKeyDate, today);
    } else if (frequency === "day") {
      const today = new Date().toDateString();
      sessionStorage.setItem(CONFIG.storageKeyDate, today);
    }
  }

  // Get rewards data from the JSON script tag
  function getRewardsData() {
    try {
      const scriptElement = document.getElementById("birthday-rewards-data");
      if (scriptElement) {
        const data = JSON.parse(scriptElement.textContent);
        const rewards = data.rewards || [];
        return rewards;
      }
    } catch (error) {
      console.error("Failed to parse birthday rewards data:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
    }
    return [];
  }

  // Check if user is B2B
  function isB2BUser() {
    try {
      const scriptElement = document.getElementById("birthday-rewards-data");
      if (scriptElement) {
        const data = JSON.parse(scriptElement.textContent);
        return data.isB2B || false;
      }
    } catch (error) {
      console.warn("Failed to parse B2B status:", error);
    }
    return false;
  }

  function getCustomerTags() {
    // Try multiple methods to get customer tags
    if (
      window.Shopify &&
      window.Shopify.customer &&
      window.Shopify.customer.tags
    ) {
      return window.Shopify.customer.tags;
    }

    const metaTag = document.querySelector('meta[name="customer-tags"]');
    if (metaTag && metaTag.content) {
      return metaTag.content.split(",").map((tag) => tag.trim());
    }

    return "Not logged in";
  }

  // Assign random card images to the front of each card
  function assignCardImages() {
    try {
      const scriptElement = document.getElementById("birthday-rewards-data");
      if (!scriptElement) {
        console.warn("Birthday rewards data script element not found");
        return;
      }

      const data = JSON.parse(scriptElement.textContent);
      const cardImages = data.cardImages || [];

      if (cardImages.length === 0) {
        console.warn("No card images found in data");
        return;
      }

      // Assign images to cards in order (no shuffling)
      state.cards.forEach((card, index) => {
        const frontElement = card.querySelector(".birthday-card-front");
        if (frontElement) {
          // Use the image from the array, or fallback to a default image
          const imageUrl =
            cardImages[index] ||
            cardImages[0] ||
            "//cdn.shopify.com/s/files/1/0000/0000/products/placeholder.png";

          frontElement.style.backgroundImage = `url(${imageUrl})`;
          frontElement.style.backgroundSize = "cover";
          frontElement.style.backgroundPosition = "center";
          frontElement.style.backgroundRepeat = "no-repeat";

          // Add error handling for broken images
          const img = new Image();
          img.onerror = () => {
            console.warn(
              `Image failed to load for card ${index}, using fallback`
            );
            frontElement.style.backgroundImage = `url('//cdn.shopify.com/s/files/1/0000/0000/products/placeholder.png')`;
          };
          img.src = imageUrl;
        } else {
          console.warn(`Could not find front element for card ${index}:`, {
            cardElement: !!card,
          });
        }
      });
    } catch (error) {
      console.error("Failed to assign card images:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
    }
  }

  // Setup event listeners
  function setupEventListeners() {
    if (!state.modal) return;

    // Close button
    const closeBtn = utils.getElement(CONFIG.selectors.closeBtn);
    if (closeBtn) {
      closeBtn.addEventListener("click", hideModal);
    }

    // Backdrop click
    const backdrop = utils.getElement(CONFIG.selectors.backdrop);
    if (backdrop) {
      backdrop.addEventListener("click", hideModal);
    }

    // Assign grid areas to cards and set up event listeners
    state.cards.forEach((card, index) => {
      // Assign grid area based on index
      card.style.gridArea = `card${index}`;

      card.addEventListener("click", () => handleCardClick(card));
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick(card);
        }
      });
    });

    // Keyboard navigation
    document.addEventListener("keydown", handleKeyboardNavigation);

    // Reopen button
    const reopenBtn = utils.getElement(CONFIG.selectors.reopenBtn, document);
    if (reopenBtn) {
      reopenBtn.addEventListener("click", showModal);
    }
  }

  // Handle card click
  function handleCardClick(card) {
    if (state.isActive || card.classList.contains("flipped")) return;

    state.isActive = true;
    state.selectedCard = card;

    // Select random reward
    const reward = selectRandomReward();

    // On mobile, let other cards fade out first, then expand from current position
    if (window.innerWidth <= CONFIG.mobileBreakpoint) {
      // Make other cards fade out first
      state.cards.forEach((otherCard) => {
        if (otherCard !== card) {
          otherCard.classList.add("inactive");
          otherCard.style.pointerEvents = "none";
        }
      });

      // Wait for fade out, then expand the selected card from its current position
      setTimeout(() => {
        // Hide other cards completely
        state.cards.forEach((otherCard) => {
          if (otherCard !== card) {
            otherCard.style.display = "none";
          }
        });

        // Remove gap and expand the selected card from its current position
        const cardsContainer = utils.getElement(
          CONFIG.selectors.cardsContainer
        );
        if (cardsContainer) {
          cardsContainer.classList.add("gap-removed");
        }
        card.classList.add("mobile-expanded");
      }, CONFIG.animationDelays.fadeOut);

      // Show reward before flip animation
      setTimeout(() => {
        showReward(reward);
      }, CONFIG.animationDelays.fadeOut + CONFIG.animationDelays.expansion - 100); // Buffer

      // Flip the card after reward is prepared
      setTimeout(() => {
        card.classList.add("flipped");
        card.style.pointerEvents = "auto";
      }, CONFIG.animationDelays.fadeOut + CONFIG.animationDelays.expansion);
    } else {
      // Desktop behavior - show reward first, then flip
      // Make other cards inactive
      state.cards.forEach((otherCard) => {
        if (otherCard !== card) {
          otherCard.classList.add("inactive");
          otherCard.style.pointerEvents = "none";
        }
      });

      // Show reward before flip
      showReward(reward);

      // Flip the card after reward is prepared
      setTimeout(() => {
        card.classList.add("flipped");
        card.style.pointerEvents = "auto";
      }, CONFIG.animationDelays.flip);
    }

    // Mark as seen and track that a card has been flipped
    markOfferAsSeen();
    state.hasFlippedCard = true;
  }

  // Select random reward based on weights using cumulative weight method
  function selectRandomReward() {
    if (state.rewards.length === 0) {
      return {
        type: "notice",
        message: "No rewards available at this time. Please check back later!",
      };
    }

    // Calculate total weight
    const totalWeight = state.rewards.reduce(
      (sum, reward) => sum + (reward.weight || 1),
      0
    );

    // Generate random number
    const random = Math.random() * totalWeight;

    // Find the reward based on weight
    let currentWeight = 0;
    for (const reward of state.rewards) {
      currentWeight += reward.weight || 1;
      if (random <= currentWeight) {
        return reward;
      }
    }

    // Fallback to first reward
    return state.rewards[0];
  }

  // Show the selected reward
  function showReward(reward) {
    const rewardDisplay = utils.getElement(CONFIG.selectors.rewardDisplay);
    const continueBtn = utils.getElement(CONFIG.selectors.continueBtn);

    if (!rewardDisplay || !state.selectedCard) return;

    // Update the message based on reward type
    const message = utils.getElement(CONFIG.selectors.message);
    if (message) {
      if (reward.type === "discount") {
        message.textContent = "Congratulations, you have received a discount!";
      } else if (reward.type === "product") {
        message.textContent = "Congratulations, you have received a free gift!";
      } else if (reward.type === "bundle") {
        message.textContent =
          "Congratulations, you have received a free bundle!";
      } else if (reward.type === "notice") {
        message.textContent =
          "No rewards available at this time. Please check back later!";
      }
    }

    // Move reward content to the flipped card
    const cardBack = state.selectedCard.querySelector(".birthday-card-back");
    if (cardBack) {
      // Clear the back content
      cardBack.innerHTML = "";

      // Check if reward has a custom image
      if (reward.reward_image) {
        // Add class to remove padding when using background images
        cardBack.classList.add("has-background");
        if (reward.type === "discount") {
          // For discount rewards with custom image, create full background layout
          const backgroundContainer = document.createElement("div");
          backgroundContainer.className = "birthday-reward-background";
          backgroundContainer.style.backgroundImage = `url('${reward.reward_image}')`;

          // Create copy button overlay
          const copyBtnOverlay = document.createElement("button");
          copyBtnOverlay.className = "birthday-copy-button-overlay";
          copyBtnOverlay.textContent = "Copy Code";
          copyBtnOverlay.dataset.code = reward.code;
          copyBtnOverlay.addEventListener("click", handleCopyCode);

          backgroundContainer.appendChild(copyBtnOverlay);
          cardBack.appendChild(backgroundContainer);
        } else if (reward.type === "product") {
          // For product rewards with custom image, create full background layout
          const backgroundContainer = document.createElement("div");
          backgroundContainer.className = "birthday-reward-background";
          backgroundContainer.style.backgroundImage = `url('${reward.reward_image}')`;

          // Create add to cart button overlay
          const addToCartBtnOverlay = document.createElement("button");
          addToCartBtnOverlay.className = "birthday-add-to-cart-button-overlay";
          addToCartBtnOverlay.textContent = "Get My Gift";
          addToCartBtnOverlay.dataset.productId = reward.product_id;
          addToCartBtnOverlay.dataset.productType =
            reward.product_type || "addon";
          if (reward.variant_id) {
            addToCartBtnOverlay.dataset.variantId = reward.variant_id;
          }
          addToCartBtnOverlay.addEventListener("click", () =>
            handleAddToCart(addToCartBtnOverlay)
          );

          backgroundContainer.appendChild(addToCartBtnOverlay);
          cardBack.appendChild(backgroundContainer);
        } else if (reward.type === "bundle") {
          // For bundle rewards with custom image, create full background layout
          const backgroundContainer = document.createElement("div");
          backgroundContainer.className = "birthday-reward-background";
          backgroundContainer.style.backgroundImage = `url('${reward.reward_image}')`;

          // Create add bundle button overlay
          const addBundleBtnOverlay = document.createElement("button");
          addBundleBtnOverlay.className = "birthday-add-to-cart-button-overlay";
          addBundleBtnOverlay.textContent = "Get My Gift";
          addBundleBtnOverlay.dataset.bundleData = JSON.stringify(reward);
          addBundleBtnOverlay.dataset.productType =
            reward.product_type || "addon";
          addBundleBtnOverlay.addEventListener("click", () =>
            handleAddBundle(addBundleBtnOverlay)
          );

          backgroundContainer.appendChild(addBundleBtnOverlay);
          cardBack.appendChild(backgroundContainer);
        } else {
          // For other reward types, use the original image display
          const rewardImage = document.createElement("div");
          rewardImage.className = "birthday-reward-image";
          rewardImage.innerHTML = `<img src="${reward.reward_image}" alt="${
            reward.label || reward.title
          }" style="max-width: 100%; height: auto; border-radius: 8px;">`;
          cardBack.appendChild(rewardImage);
        }
      } else {
        // For rewards without custom images, always show the appropriate content
        if (reward.type === "bundle") {
          // For bundle rewards without custom image, create the bundle display structure
          const bundleContainer = document.createElement("div");
          bundleContainer.className = "birthday-reward-bundle";
          bundleContainer.style.display = "block";

          // Create bundle icon
          const bundleIcon = document.createElement("div");
          bundleIcon.className = "birthday-reward-icon";
          bundleIcon.textContent = "🎁";
          bundleContainer.appendChild(bundleIcon);

          // Create bundle title
          const bundleTitle = document.createElement("h3");
          bundleTitle.className = "birthday-reward-title";
          bundleTitle.textContent = "Free Bundle!";
          bundleContainer.appendChild(bundleTitle);

          // Create bundle products section
          const bundleProductsSection = document.createElement("div");
          bundleProductsSection.className = "birthday-bundle-products";
          bundleProductsSection.style.textAlign = "center";
          bundleProductsSection.style.margin = "15px 0";

          // Create bundle products grid
          const bundleProductsGrid = document.createElement("div");
          bundleProductsGrid.className = "bundle-products-grid";
          bundleProductsGrid.style.display = "grid";
          bundleProductsGrid.style.gridTemplateColumns =
            "repeat(auto-fit, minmax(80px, 1fr))";
          bundleProductsGrid.style.gap = "10px";
          bundleProductsGrid.style.justifyItems = "center";

          // Add products to grid if they exist
          if (reward.products && reward.products.length > 0) {
            const validProducts = reward.products.filter(
              (product) => product && product.product_id && product.title
            ); // Filter out empty products

            if (validProducts.length > 0) {
              const productsHTML = validProducts
                .map(
                  (product) => `
                  <div class="bundle-product-item" style="
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin: 10px;
                    padding: 10px;
                    border: 1px solid #eee;
                    border-radius: 8px;
                    background: #f9f9f9;
                  ">
                    <img src="${
                      product.image_url ||
                      "//cdn.shopify.com/s/files/1/0000/0000/products/placeholder.png"
                    }" 
                         alt="${product.title}" 
                         style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;"
                         onerror="this.src='//cdn.shopify.com/s/files/1/0000/0000/products/placeholder.png'">
                    <span style="font-size: 12px; text-align: center; color: #666;">${
                      product.title
                    }</span>
                  </div>
                `
                )
                .join("");

              bundleProductsGrid.innerHTML = productsHTML;
            } else {
              // If no valid products, show a placeholder message
              bundleProductsGrid.innerHTML = `
                <div style="
                  text-align: center;
                  color: #666;
                  font-style: italic;
                  padding: 20px;
                ">
                  Bundle products will be added to your cart
                </div>
              `;
            }
          } else {
            // If no products array, show a placeholder message
            bundleProductsGrid.innerHTML = `
              <div style="
                text-align: center;
                color: #666;
                font-style: italic;
                padding: 20px;
              ">
                Bundle products will be added to your cart
              </div>
            `;
          }

          bundleProductsSection.appendChild(bundleProductsGrid);
          bundleContainer.appendChild(bundleProductsSection);

          // Create bundle title text
          const bundleTitleText = document.createElement("p");
          bundleTitleText.className = "birthday-bundle-title";
          bundleTitleText.textContent =
            reward.bundle_title || "Birthday Bundle";
          bundleContainer.appendChild(bundleTitleText);

          // Create add bundle button
          const addBundleBtn = document.createElement("button");
          addBundleBtn.className = "birthday-add-to-cart-button";
          addBundleBtn.textContent = "Get My Gift";
          addBundleBtn.dataset.bundleData = JSON.stringify(reward);
          addBundleBtn.dataset.productType = reward.product_type || "addon";
          addBundleBtn.addEventListener("click", () =>
            handleAddBundle(addBundleBtn)
          );
          bundleContainer.appendChild(addBundleBtn);

          cardBack.appendChild(bundleContainer);
        } else {
          // For other reward types without custom images, use the original display
          const rewardContent = rewardDisplay.cloneNode(true);
          rewardContent.style.display = "flex";
          rewardContent.classList.add("birthday-reward-in-card");
          cardBack.appendChild(rewardContent);
        }
      }
    }

    // Show the appropriate reward type
    if (reward.type === "discount") {
      showDiscountReward(reward);
    } else if (reward.type === "product") {
      showProductReward(reward);
    } else if (reward.type === "bundle") {
      showBundleReward(reward);
    } else if (reward.type === "notice") {
      showNoticeReward(reward);
    }

    // Show continue button
    if (continueBtn) {
      continueBtn.style.display = "inline-block";

      // Add event listener to continue button
      const continueBtnInCard = state.selectedCard.querySelector(
        CONFIG.selectors.continueBtn
      );
      if (continueBtnInCard) {
        continueBtnInCard.addEventListener("click", hideModal);
      }
    }

    // Track analytics
    trackRewardSelection(reward);
  }

  // Show discount reward
  function showDiscountReward(reward) {
    // Find discount container in the flipped card
    const discountContainer = state.selectedCard.querySelector(
      CONFIG.selectors.discountContainer
    );
    const codeText = state.selectedCard.querySelector(
      CONFIG.selectors.codeText
    );
    const rewardTitle = state.selectedCard.querySelector(
      CONFIG.selectors.rewardTitle
    );

    if (discountContainer && codeText) {
      codeText.textContent = reward.code;
      if (rewardTitle && reward.label) {
        rewardTitle.textContent = reward.label;
      }
      discountContainer.style.display = "block";

      // Add event listener to copy button
      const copyBtn = state.selectedCard.querySelector(
        CONFIG.selectors.copyBtn
      );
      if (copyBtn) {
        copyBtn.addEventListener("click", handleCopyCode);
      }
    }
  }

  // Show notice reward (when no rewards are available)
  function showNoticeReward(reward) {
    // Find notice container in the flipped card
    const noticeContainer = state.selectedCard.querySelector(
      CONFIG.selectors.noticeContainer
    );
    const noticeText = state.selectedCard.querySelector(
      CONFIG.selectors.noticeText
    );

    if (noticeContainer && noticeText) {
      // Ensure the notice container is visible
      noticeContainer.style.display = "block";

      // Set the notice message
      noticeText.textContent = reward.message;
    }
  }

  // Show product reward
  function showProductReward(reward) {
    // Find product container in the flipped card
    const productContainer = state.selectedCard.querySelector(
      CONFIG.selectors.productContainer
    );
    const productTitle = state.selectedCard.querySelector(
      CONFIG.selectors.productTitle
    );
    const addToCartBtn = state.selectedCard.querySelector(
      CONFIG.selectors.addToCartBtn
    );

    if (productContainer) {
      // Ensure the product container is visible
      productContainer.style.display = "block";

      if (productTitle && reward.title) {
        productTitle.textContent = reward.title;
      }

      if (addToCartBtn && reward.product_id) {
        addToCartBtn.dataset.productId = reward.product_id;
        addToCartBtn.dataset.productType = reward.product_type || "addon";
        // Also set the variant ID from the reward data
        if (reward.variant_id) {
          addToCartBtn.dataset.variantId = reward.variant_id;
        } else {
          // Clear any existing variant ID if none specified
          delete addToCartBtn.dataset.variantId;
        }

        console.log("Product reward selected:", {
          product_id: reward.product_id,
          variant_id: reward.variant_id,
          title: reward.title,
          image_url: reward.image_url,
          product_type: reward.product_type || "addon",
        });
      }

      // Add the product image to the dedicated image container
      const productImageContainer = productContainer.querySelector(
        CONFIG.selectors.productImage
      );
      if (productImageContainer && reward.image_url) {
        productImageContainer.innerHTML = `<img src="${reward.image_url}" alt="${reward.title}">`;
      }

      // Update the title to show if it's an add-on or standalone
      if (productTitle && reward.title) {
        const productType = reward.product_type || "addon";
        if (productType === "addon") {
          productTitle.textContent = `${reward.title} (Add-on Gift)`;
        } else {
          productTitle.textContent = `${reward.title} (Free Gift)`;
        }
      }

      // Add event listener to add to cart button
      const addToCartBtnInCard = state.selectedCard.querySelector(
        CONFIG.selectors.addToCartBtn
      );
      if (addToCartBtnInCard) {
        addToCartBtnInCard.addEventListener("click", () =>
          handleAddToCart(addToCartBtnInCard)
        );
      }
    }
  }

  // Show bundle reward
  function showBundleReward(reward) {
    // Find bundle container in the flipped card
    const bundleContainer = state.selectedCard.querySelector(
      CONFIG.selectors.bundleContainer
    );
    const bundleTitle = state.selectedCard.querySelector(
      CONFIG.selectors.bundleTitle
    );
    const addBundleBtn = state.selectedCard.querySelector(
      CONFIG.selectors.addBundleBtn
    );
    const bundleProductsGrid = state.selectedCard.querySelector(
      CONFIG.selectors.bundleProductsGrid
    );

    // If bundle container exists (from template), update it
    if (bundleContainer) {
      // Ensure the bundle container is visible
      bundleContainer.style.display = "block";

      if (bundleTitle && reward.bundle_title) {
        bundleTitle.textContent = reward.bundle_title;
      }

      if (addBundleBtn && reward.products) {
        addBundleBtn.dataset.bundleData = JSON.stringify(reward);
        addBundleBtn.dataset.productType = reward.product_type || "addon";

        console.log("Bundle reward selected:", {
          bundle_title: reward.bundle_title,
          products: reward.products,
          product_type: reward.product_type || "addon",
        });
      }

      // Display bundle products in grid
      if (bundleProductsGrid && reward.products) {
        const productsHTML = reward.products
          .filter((product) => product && product.product_id && product.title) // Filter out empty products
          .map(
            (product) => `
          <div class="bundle-product-item" style="
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: 10px;
            padding: 10px;
            border: 1px solid #eee;
            border-radius: 8px;
            background: #f9f9f9;
          ">
            <img src="${
              product.image_url ||
              "//cdn.shopify.com/s/files/1/0000/0000/products/placeholder.png"
            }" 
                 alt="${product.title}" 
                 style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;"
                 onerror="this.src='//cdn.shopify.com/s/files/1/0000/0000/products/placeholder.png'">
            <span style="
              font-size: 12px;
              text-align: center;
              color: #666;
            ">${product.title}</span>
          </div>
        `
          )
          .join("");

        bundleProductsGrid.innerHTML = productsHTML;
        bundleProductsGrid.style.display = "grid";
        bundleProductsGrid.style.gridTemplateColumns =
          "repeat(auto-fit, minmax(80px, 1fr))";
        bundleProductsGrid.style.gap = "10px";
        bundleProductsGrid.style.justifyItems = "center";
      }

      // Update the title to show if it's an add-on or standalone
      if (bundleTitle && reward.bundle_title) {
        const productType = reward.product_type || "addon";
        if (productType === "addon") {
          bundleTitle.textContent = `${reward.bundle_title} (Add-on Bundle)`;
        } else {
          bundleTitle.textContent = `${reward.bundle_title} (Free Bundle)`;
        }
      }

      // Add event listener to add bundle button
      const addBundleBtnInCard = state.selectedCard.querySelector(
        CONFIG.selectors.addBundleBtn
      );
      if (addBundleBtnInCard) {
        addBundleBtnInCard.addEventListener("click", () =>
          handleAddBundle(addBundleBtnInCard)
        );
      }
    } else {
      // If bundle container doesn't exist (dynamically created), the content is already set up
      // Just ensure any dynamically created bundle buttons have event listeners
      const addBundleBtnInCard = state.selectedCard.querySelector(
        CONFIG.selectors.addBundleBtn
      );
      if (addBundleBtnInCard) {
        addBundleBtnInCard.addEventListener("click", () =>
          handleAddBundle(addBundleBtnInCard)
        );
      }
    }
  }

  // Handle copy code
  async function handleCopyCode() {
    // Check if this is the overlay button (has data-code attribute)
    const isOverlayButton = this.dataset && this.dataset.code;
    let codeToCopy, copyBtn;

    if (isOverlayButton) {
      // Use the code from the overlay button's data attribute
      codeToCopy = this.dataset.code;
      copyBtn = this;
    } else {
      // Use the original method with code text element
      const codeText = utils.getElement(CONFIG.selectors.codeText);
      copyBtn = utils.getElement(CONFIG.selectors.copyBtn);

      if (!codeText || !copyBtn) return;
      codeToCopy = codeText.textContent;
    }

    try {
      await navigator.clipboard.writeText(codeToCopy);
      copyBtn.textContent = "Copied!";
      copyBtn.classList.add("copied");

      setTimeout(() => {
        copyBtn.textContent = "Copy Code";
        copyBtn.classList.remove("copied");
      }, 2000);
    } catch (error) {
      console.warn("Failed to copy code:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = codeToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      copyBtn.textContent = "Copied!";
      copyBtn.classList.add("copied");

      setTimeout(() => {
        copyBtn.textContent = "Copy Code";
        copyBtn.classList.remove("copied");
      }, 2000);
    }
  }

  // Handle add to cart with debouncing
  const handleAddToCart = utils.debounce(async function (buttonElement) {
    if (state.isProcessing) return;
    state.isProcessing = true;

    // Use the passed button element or find it via selector
    const addToCartBtn =
      buttonElement || utils.getElement(CONFIG.selectors.addToCartBtn);
    const productId = addToCartBtn?.dataset.productId;
    const variantId = addToCartBtn?.dataset.variantId;
    const productType = addToCartBtn?.dataset.productType || "addon";

    console.log(
      "handleAddToCart called with productId:",
      productId,
      "variantId:",
      variantId,
      "productType:",
      productType
    );

    if (!productId) {
      console.warn("No product ID found for free product");
      addToCartBtn.textContent = "No Product Configured";
      state.isProcessing = false;
      return;
    }

    // Check if product ID looks like a placeholder
    if (productId === "123456789" || productId === "") {
      console.warn(
        "Placeholder product ID detected. Please configure a real product in theme settings."
      );
      addToCartBtn.textContent = "Product Not Configured";
      addToCartBtn.style.background = "#dc3545";
      state.isProcessing = false;
      return;
    }

    // Show loading state
    addToCartBtn.classList.add("loading");
    addToCartBtn.textContent = "Adding...";

    try {
      let targetVariantId = variantId;

      // Validate that we have a variant ID
      if (
        !targetVariantId ||
        targetVariantId === "null" ||
        targetVariantId === ""
      ) {
        console.error(
          "No variant ID provided. Please configure a variant ID in theme settings."
        );
        addToCartBtn.textContent = "Variant Not Configured";
        addToCartBtn.style.background = "#dc3545";
        state.isProcessing = false;
        return;
      }

      console.log("Using specified variant ID:", targetVariantId);

      // Prepare properties using optimized approach
      const properties = {};
      properties[
        CONFIG.propertyMessages[productType] ||
          CONFIG.propertyMessages.standalone
      ] = "✓";

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: targetVariantId,
          quantity: 1,
          properties: {
            ...properties,
          },
        }),
      });

      if (response.ok) {
        const result = await response.json();
        addToCartBtn.textContent = "Added to cart!";
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add("disabled");

        // Fetch updated cart to see if script applied discount
        try {
          const cartResponse = await fetch("/cart.js");
          if (cartResponse.ok) {
            const cartData = await cartResponse.json();
          }
        } catch (cartError) {
          console.log("🛒 Could not fetch updated cart:", cartError);
        }

        // Update cart count if it exists
        updateCartCount();

        // Track successful add to cart
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "birthday_offer_add_to_cart",
            product_id: productId,
            variant_id: targetVariantId,
            product_type: productType,
          });
        }

        // Wait 2 seconds, then close modal and open mini cart
        setTimeout(() => {
          hideModal();
          // Add a small delay before opening cart to ensure modal is fully closed
          setTimeout(() => {
            openMiniCart();
          }, 100);
        }, 2000);
      } else {
        // Use optimized error handling
        handleCartError(response, addToCartBtn);
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      handleNetworkError(error, addToCartBtn);
    } finally {
      addToCartBtn.classList.remove("loading");
      state.isProcessing = false;
    }
  }, 300); // 300ms debounce

  // Handle add bundle to cart with debouncing
  const handleAddBundle = utils.debounce(async function (buttonElement) {
    if (state.isProcessing) return;
    state.isProcessing = true;

    // Use the passed button element or find it via selector
    const addBundleBtn =
      buttonElement || utils.getElement(CONFIG.selectors.addBundleBtn);
    const bundleData = addBundleBtn?.dataset.bundleData;
    const productType = addBundleBtn?.dataset.productType || "addon";

    if (bundleData) {
      try {
        const parsedBundle = JSON.parse(bundleData);
      } catch (e) {
        console.warn("Could not parse bundle data for logging:", e);
      }
    }

    if (!bundleData) {
      console.warn("No bundle data found");
      addBundleBtn.textContent = "Bundle Not Configured";
      state.isProcessing = false;
      return;
    }

    let bundle;
    try {
      bundle = JSON.parse(bundleData);
    } catch (error) {
      console.error("Error parsing bundle data:", error);
      addBundleBtn.textContent = "Bundle Data Error";
      state.isProcessing = false;
      return;
    }

    if (!bundle.products || bundle.products.length < 2) {
      console.warn("Bundle must have at least 2 products");
      addBundleBtn.textContent = "Bundle Configuration Error";
      state.isProcessing = false;
      return;
    }

    // Show loading state
    addBundleBtn.classList.add("loading");
    addBundleBtn.textContent = "Adding...";

    try {
      // Add each product in the bundle to cart sequentially
      const results = [];
      for (const product of bundle.products) {
        // Validate that we have a variant ID
        if (
          !product.variant_id ||
          product.variant_id === "null" ||
          product.variant_id === ""
        ) {
          throw new Error(
            `No variant ID provided for product: ${product.title}`
          );
        }

        // Prepare properties for bundle items
        const properties = {};
        properties[
          CONFIG.propertyMessages[productType] ||
            CONFIG.propertyMessages.standalone
        ] = "✓";

        const cartData = {
          id: product.variant_id,
          quantity: 1,
          properties: {
            ...properties,
          },
        };

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cartData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Cart add failed for product:", product.title, {
            status: response.status,
            statusText: response.statusText,
            error: errorText,
          });
          throw new Error(
            `Failed to add product ${product.title} to cart: ${response.status} ${response.statusText}`
          );
        }

        const result = await response.json();
        results.push(result);

        // Add a small delay between requests to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      addBundleBtn.textContent = "Added to cart!";
      addBundleBtn.disabled = true;
      addBundleBtn.classList.add("disabled");

      // Fetch updated cart to see if script applied discount
      try {
        const cartResponse = await fetch("/cart.js");
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
        }
      } catch (cartError) {
        console.log("🛒 Could not fetch updated cart:", cartError);
      }

      // Update cart count if it exists
      updateCartCount();

      // Track successful bundle add to cart
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "birthday_offer_bundle_added",
          bundle_title: bundle.bundle_title,
          product_count: bundle.products.length,
          product_type: productType,
        });
      }

      // Wait 0.5 seconds, then close modal and open mini cart
      setTimeout(() => {
        hideModal();
        // Add a small delay before opening cart to ensure modal is fully closed
        setTimeout(() => {
          openMiniCart();
        }, 100);
      }, 500);
    } catch (error) {
      console.error("Error adding bundle to cart:", error);
      addBundleBtn.textContent = "Error Adding Bundle";
      addBundleBtn.style.background = "#dc3545";
    } finally {
      addBundleBtn.classList.remove("loading");
      state.isProcessing = false;
    }
  }, 300); // 300ms debounce

  // Optimized error handling functions
  function handleCartError(response, addToCartBtn) {
    const errorData = response.json().catch(() => ({}));
    console.error("Cart add failed:", {
      status: response.status,
      statusText: response.statusText,
      error: errorData,
    });

    const errorMessage =
      CONFIG.errorMessages[response.status] || "Error - Try Again";
    addToCartBtn.textContent = errorMessage;
    addToCartBtn.disabled = true;
    addToCartBtn.classList.add("disabled");
  }

  function handleNetworkError(error, addToCartBtn) {
    let errorMessage = CONFIG.errorMessages.network;

    if (error.message.includes("Failed to fetch")) {
      errorMessage = CONFIG.errorMessages.network;
    } else if (error.message.includes("Product not found")) {
      errorMessage = CONFIG.errorMessages.productNotFound;
    } else if (error.message.includes("No variants found")) {
      errorMessage = CONFIG.errorMessages.configError;
    } else if (error.message.includes("Failed to fetch product data")) {
      errorMessage = CONFIG.errorMessages.dataError;
    }

    addToCartBtn.textContent = errorMessage;
    addToCartBtn.disabled = true;
    addToCartBtn.classList.add("disabled");
  }

  // Optimized mini cart opening
  function openMiniCart() {
    // Check if cart is already open to prevent conflicts
    const cartDrawer = document.querySelector(CONFIG.selectors.cartDrawer);
    if (cartDrawer) {
      const detailsElement = cartDrawer.querySelector("details");
      if (detailsElement && detailsElement.hasAttribute("open")) {
        return;
      }
    }

    const methods = [
      () => {
        const miniCart = document.querySelector(CONFIG.selectors.miniCart);
        if (miniCart && typeof miniCart.open === "function") {
          miniCart.open();
          return true;
        }
        return false;
      },
      () => {
        const cartDrawer = document.querySelector(CONFIG.selectors.cartDrawer);
        if (cartDrawer) {
          const summaryElement = cartDrawer.querySelector("summary");
          if (summaryElement) {
            summaryElement.click();
            return true;
          }
        }
        return false;
      },
      () => {
        const cartDrawerDetails = document.querySelector(
          CONFIG.selectors.cartDrawer
        );
        if (cartDrawerDetails) {
          const detailsElement = cartDrawerDetails.querySelector("details");
          if (detailsElement && !detailsElement.hasAttribute("open")) {
            detailsElement.setAttribute("open", "");
            return true;
          }
        }
        return false;
      },
      () => {
        const cartIcon = document.querySelector(CONFIG.selectors.cartIcon);
        if (cartIcon) {
          cartIcon.click();
          return true;
        }
        return false;
      },
    ];

    // Try each method until one succeeds
    for (const method of methods) {
      if (method()) {
        return;
      }
    }

    // Fallback: Trigger custom events
    document.dispatchEvent(new CustomEvent("cart-drawer:toggle"));
    document.dispatchEvent(new CustomEvent("cart-drawer:open"));
  }

  // Update cart count (if cart bubble exists)
  function updateCartCount() {
    const cartBubble = utils.getElement(CONFIG.selectors.cartBubble, document);
    if (cartBubble) {
      // Trigger cart update if theme has cart update functionality
      if (window.theme && window.theme.updateCartCount) {
        window.theme.updateCartCount();
      }
    }
  }

  // Handle keyboard navigation
  function handleKeyboardNavigation(e) {
    if (!state.modal || !state.isActive) return;

    if (e.key === "Escape") {
      hideModal();
    }
  }

  // Show modal
  function showModal() {
    if (!state.modal) return;

    state.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    // Hide reopen button
    const reopenBtn = utils.getElement(CONFIG.selectors.reopenBtn, document);
    if (reopenBtn) {
      reopenBtn.classList.remove("show");
    }

    // Focus management
    const firstCard = state.cards[0];
    if (firstCard) {
      firstCard.focus();
    }
  }

  // Hide modal with improved cleanup
  function hideModal() {
    if (!state.modal) return;

    state.modal.style.display = "none";
    document.body.style.overflow = "";

    // Reset state
    state.isActive = false;
    state.selectedCard = null;

    // Reset cards
    state.cards.forEach((card) => {
      card.classList.remove("flipped", "faded");
    });

    // Hide reward display
    const rewardDisplay = utils.getElement(CONFIG.selectors.rewardDisplay);
    if (rewardDisplay) {
      rewardDisplay.style.display = "none";
    }

    // Show cards again
    const cardsContainer = utils.getElement(CONFIG.selectors.cardsContainer);
    if (cardsContainer) {
      cardsContainer.style.display = "grid";
    }

    // Hide continue button
    const continueBtn = utils.getElement(CONFIG.selectors.continueBtn);
    if (continueBtn) {
      continueBtn.style.display = "none";
    }

    // Hide reward containers
    const discountContainer = utils.getElement(
      CONFIG.selectors.discountContainer
    );
    const productContainer = utils.getElement(
      CONFIG.selectors.productContainer
    );
    const bundleContainer = utils.getElement(CONFIG.selectors.bundleContainer);
    if (discountContainer) discountContainer.style.display = "none";
    if (productContainer) productContainer.style.display = "none";
    if (bundleContainer) bundleContainer.style.display = "none";

    // Clean up event listeners by cloning buttons
    const buttons = state.modal.querySelectorAll("button");
    buttons.forEach((btn) => {
      btn.replaceWith(btn.cloneNode(true));
    });

    // Clear element cache
    utils.clearElementCache();

    // Show reopen button if no card was flipped
    if (!state.hasFlippedCard) {
      const reopenBtn = utils.getElement(CONFIG.selectors.reopenBtn, document);
      if (reopenBtn) {
        reopenBtn.classList.add("show");
      }
    }
  }

  // Track reward selection for analytics
  function trackRewardSelection(reward) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "birthday_offer_reward_selected",
        reward_type: reward.type,
        reward_code: reward.code || reward.variant_id,
      });
    }
  }

  // Cleanup function
  function cleanup() {
    if (state.modal) {
      // Remove event listeners
      const closeBtn = utils.getElement(CONFIG.selectors.closeBtn);
      if (closeBtn) {
        closeBtn.removeEventListener("click", hideModal);
      }

      document.removeEventListener("keydown", handleKeyboardNavigation);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      init().catch(console.error)
    );
  } else {
    init().catch(console.error);
  }

  // Cleanup on page unload
  window.addEventListener("beforeunload", cleanup);

  // Reset function for testing
  function resetBirthdayOffer() {
    // Clear all stored data
    sessionStorage.removeItem(CONFIG.storageKey);
    sessionStorage.removeItem(CONFIG.storageKeyDate);

    // Reset modal state
    if (state.modal) {
      state.modal.style.display = "none";
      document.body.style.overflow = "";

      // Reset cards
      state.cards.forEach((card) => {
        card.classList.remove("flipped", "faded");
      });

      // Hide reward display
      const rewardDisplay = utils.getElement(CONFIG.selectors.rewardDisplay);
      if (rewardDisplay) {
        rewardDisplay.style.display = "none";
      }

      // Show cards again
      const cardsContainer = utils.getElement(CONFIG.selectors.cardsContainer);
      if (cardsContainer) {
        cardsContainer.style.display = "grid";
      }

      // Hide continue button
      const continueBtn = utils.getElement(CONFIG.selectors.continueBtn);
      if (continueBtn) {
        continueBtn.style.display = "none";
      }

      // Hide reward containers
      const discountContainer = utils.getElement(
        CONFIG.selectors.discountContainer
      );
      const productContainer = utils.getElement(
        CONFIG.selectors.productContainer
      );
      const bundleContainer = utils.getElement(
        CONFIG.selectors.bundleContainer
      );
      if (discountContainer) discountContainer.style.display = "none";
      if (productContainer) productContainer.style.display = "none";
      if (bundleContainer) bundleContainer.style.display = "none";
    }

    // Reset state
    state.isActive = false;
    state.selectedCard = null;
    state.isProcessing = false;
    state.hasFlippedCard = false;

    // Hide reopen button
    const reopenBtn = utils.getElement(CONFIG.selectors.reopenBtn, document);
    if (reopenBtn) {
      reopenBtn.classList.remove("show");
    }

    // Clear element cache
    utils.clearElementCache();

    // Show success message
    alert("Birthday offer reset! Refresh the page to see it again.");
  }

  // Expose for debugging
  window.birthdayOffer = {
    init,
    hideModal,
    showModal,
    cleanup,
    reset: resetBirthdayOffer,
    assignCardImages, // Expose card image assignment for debugging
    utils, // Expose utils for debugging
    CONFIG, // Expose config for debugging
  };

  // Expose reset function globally for the button
  window.resetBirthdayOffer = resetBirthdayOffer;

  // Expose card image assignment function for debugging
  window.reassignCardImages = assignCardImages;

  // Debug function to test product access
  window.testProductAccess = async function (productId) {
    try {
      const response = await fetch(`/products.json?ids=${productId}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Product test error:", error);
      return null;
    }
  };

  // Function to list all available products
  window.listAvailableProducts = async function () {
    try {
      let allProducts = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(`/products.json?page=${page}&limit=250`);
        const data = await response.json();

        if (data.products && data.products.length > 0) {
          allProducts = allProducts.concat(data.products);
          page++;
        } else {
          hasMore = false;
        }
      }

      return allProducts;
    } catch (error) {
      console.error("Error fetching products:", error);
      return null;
    }
  };

  // Function to find product by title
  window.findProductByTitle = async function (searchTitle) {
    try {
      // Use the listAvailableProducts function to get all products
      const allProducts = await window.listAvailableProducts();

      if (!allProducts) {
        return null;
      }

      const found = allProducts.find((p) =>
        p.title.toLowerCase().includes(searchTitle.toLowerCase())
      );

      return found;
    } catch (error) {
      console.error("Error searching products:", error);
      return null;
    }
  };
})();
